import React, { useEffect, useRef } from 'react';

export interface SpinePlayerProps {
  zipUrl: string;
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
}

const RUNTIME_URL =
  'https://web-assets.alo7.com/assets/scripts/spine-runtime/spine-3.8/spine-webgl.js';
const FIT_RATIO = 1;

const loadedScripts = new Set<string>();
const loadingScripts = new Map<string, Promise<void>>();

function releaseWebGLContext(gl: WebGLRenderingContext | null) {
  if (!gl) return;
  try {
    gl.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    // ignore
  }
}

function loadScript(url: string): Promise<void> {
  if (loadedScripts.has(url)) return Promise.resolve();
  const pending = loadingScripts.get(url);
  if (pending) return pending;

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = () => { loadedScripts.add(url); loadingScripts.delete(url); resolve(); };
    script.onerror = () => { loadingScripts.delete(url); reject(new Error('Failed to load spine runtime')); };
    document.head.appendChild(script);
  });
  loadingScripts.set(url, promise);
  return promise;
}

function getSpine(): any {
  return (window as any).spine || null;
}

async function extractZip(zipUrl: string) {
  const { strFromU8, unzipSync } = await import('fflate');
  const res = await fetch(zipUrl);
  if (!res.ok) throw new Error('Failed to fetch zip');

  const files = unzipSync(new Uint8Array(await res.arrayBuffer()));
  const fileMap = new Map<string, Uint8Array>();
  for (const [path, data] of Object.entries(files)) {
    fileMap.set(path.split('/').pop() || path, data as Uint8Array);
  }

  const atlasEntry = [...fileMap.entries()].find(([n]) => n.endsWith('.atlas'));
  const jsonEntry = [...fileMap.entries()].find(([n]) => n.endsWith('.json'));
  if (!atlasEntry || !jsonEntry) throw new Error('Missing .atlas or .json in zip');

  const blobUrls: string[] = [];
  const imageMap = new Map<string, string>();
  for (const [name, data] of fileMap.entries()) {
    if (/\.(png|jpg|jpeg|webp)$/i.test(name)) {
      const url = URL.createObjectURL(new Blob([data.buffer as ArrayBuffer], { type: 'image/png' }));
      imageMap.set(name, url);
      blobUrls.push(url);
    }
  }

  return {
    atlasText: strFromU8(atlasEntry[1]),
    jsonText: strFromU8(jsonEntry[1]),
    imageMap,
    blobUrls,
  };
}

function loadImages(imageMap: Map<string, string>): Promise<Map<string, HTMLImageElement>> {
  return Promise.all(
    [...imageMap.entries()].map(
      ([name, url]) =>
        new Promise<[string, HTMLImageElement]>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve([name, img]);
          img.onerror = () => reject(new Error(`Failed to load image: ${name}`));
          img.src = url;
        }),
    ),
  ).then(entries => new Map(entries));
}

export function SpinePlayerWebGl(props: SpinePlayerProps) {
  const { zipUrl, width = 256, height = 144, className, style } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const skeletonRef = useRef<any>(null);
  const animStateRef = useRef<any>(null);
  const glCtxRef = useRef<any>(null);
  const sceneRendererRef = useRef<any>(null);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const destroyedRef = useRef(false);
  const blobUrlsRef = useRef<string[]>([]);
  const firstAnimRef = useRef<string>('');

  function cleanupPlayer() {
    const gl = (glCtxRef.current && glCtxRef.current.gl) || null;
    cancelAnimationFrame(rafRef.current);
    skeletonRef.current = null;
    animStateRef.current = null;
    releaseWebGLContext(gl);
    glCtxRef.current = null;
    sceneRendererRef.current = null;
    blobUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    blobUrlsRef.current = [];
  }

  function playFirst() {
    const state = animStateRef.current;
    const name = firstAnimRef.current;
    if (state && name) {
      state.setAnimation(0, name, true);
    }
  }

  function renderLoop(timestamp: number) {
    if (destroyedRef.current) return;
    const skeleton = skeletonRef.current;
    const animState = animStateRef.current;
    const glCtx = glCtxRef.current;
    const renderer = sceneRendererRef.current;
    const spine = getSpine();
    if (!skeleton || !animState || !glCtx || !renderer || !spine) return;

    const delta = Math.min((timestamp - lastTimeRef.current) / 1000, 0.1);
    lastTimeRef.current = timestamp;
    animState.update(delta);
    animState.apply(skeleton);
    skeleton.updateWorldTransform();

    const resizeModes = spine.webgl && spine.webgl.ResizeMode;
    const resizeMode = resizeModes
      ? (resizeModes.Fit !== undefined ? resizeModes.Fit : resizeModes.Expand)
      : undefined;
    if (resizeMode !== undefined) renderer.resize(resizeMode);

    const gl = glCtx.gl;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    renderer.begin();
    renderer.drawSkeleton(skeleton, false);
    renderer.end();

    rafRef.current = requestAnimationFrame(renderLoop);
  }

  function buildSkeleton(atlas: any, jsonText: string) {
    const spine = getSpine();
    const canvas = canvasRef.current;
    if (!spine || !canvas) return;

    const skData = new spine.SkeletonJson(new spine.AtlasAttachmentLoader(atlas)).readSkeletonData(jsonText);
    const skeleton = new spine.Skeleton(skData);
     // 使用 CSS 显示尺寸（与 SceneRenderer.resize(Fit) 的相机视口一致）
    // canvas.width 是 WebGL framebuffer 像素尺寸，可能与 CSS 尺寸不同
    const viewW = canvas.clientWidth || canvas.width;
    const viewH = canvas.clientHeight || canvas.height;

    const scale = Math.min(
      (viewW * FIT_RATIO) / (skData.width || viewW),
      (viewH * FIT_RATIO) / (skData.height || viewH),
    );

    skeleton.scaleX = scale;
    skeleton.scaleY = scale;
    skeleton.x = -(skData.x + skData.width / 2) * scale;
    skeleton.y = -(skData.y + skData.height / 2) * scale;
    skeleton.setToSetupPose();
    skeleton.updateWorldTransform();

    const animState = new spine.AnimationState(new spine.AnimationStateData(skData));
    skeletonRef.current = skeleton;
    animStateRef.current = animState;

    firstAnimRef.current = (skData.animations[0] && skData.animations[0].name) || '';
    playFirst();

    lastTimeRef.current = performance.now();
    rafRef.current = requestAnimationFrame(renderLoop);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !zipUrl) return;
    destroyedRef.current = false;

    const run = async () => {
      try {
        await loadScript(RUNTIME_URL);
      } catch (e) {
        return;
      }
      if (destroyedRef.current) return;

      const spine = getSpine();
      if (!spine || !spine.webgl) return;

      const glCtx = new spine.webgl.ManagedWebGLRenderingContext(canvas, { alpha: true });
      const renderer = new spine.webgl.SceneRenderer(canvas, glCtx, false);
      glCtxRef.current = glCtx;
      sceneRendererRef.current = renderer;

      try {
        const { atlasText, jsonText, imageMap, blobUrls } = await extractZip(zipUrl);
        if (destroyedRef.current) { blobUrls.forEach(u => URL.revokeObjectURL(u)); return; }

        const imageElements = await loadImages(imageMap);
        if (destroyedRef.current) { blobUrls.forEach(u => URL.revokeObjectURL(u)); return; }

        blobUrlsRef.current = blobUrls;

        const atlas = new spine.TextureAtlas(atlasText, (path: string) => {
          const img = imageElements.get(path);
          if (!img) throw new Error(`Texture not found: ${path}`);
          return new spine.webgl.GLTexture(glCtx, img, false);
        });

        buildSkeleton(atlas, jsonText);
      } catch (e) {
        cleanupPlayer();
        // 加载失败静默处理，不影响页面
      }
    };

    run();

    return () => {
      destroyedRef.current = true;
      cleanupPlayer();
    };
  }, [zipUrl]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{ width: width, height: height, ...style }}
      onClick={playFirst}
    />
  );
}
